import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Scale, Receipt, MapPin, Users, Mail, Save, X,
  Plus, Trash2, ChevronDown, ShieldCheck, Paperclip, Briefcase, Wallet, Landmark, Globe2,
  Utensils, Flame, Wine, Palmtree, Lock, Eye, ExternalLink, Star,
  RefreshCw, Pencil, Coins,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useRestaurant } from '../context/RestaurantContext';
import { mastersService } from '../services/mastersService';
import apiClient from '../services/api';
import { accountsService } from '../services/accountsService';
import { locationsService } from '../services/locationsService';
import { confirm } from '../components/ui/confirm-dialog';
import { todayISO } from '../lib/utils';

const EMPLOYEE_BANDS = ['1-10', '11-50', '51-200', '201-500', '500+'];
const REMINDER_OPTIONS = [
  { value: 7, label: '7 days before' },
  { value: 14, label: '14 days before' },
  { value: 30, label: '30 days before' },
  { value: 60, label: '60 days before' },
  { value: 90, label: '90 days before' },
];

const TABS = [
  { key: 'profile', label: 'Company Profile', icon: Building2 },
  { key: 'legal', label: 'Legal & Compliance', icon: Scale },
  { key: 'mra', label: 'MRA Settings', icon: Receipt },
  { key: 'address', label: 'Addresses & Contact', icon: MapPin },
  { key: 'directory', label: 'Directory', icon: Users },
  { key: 'locations', label: 'Locations', icon: Globe2 },
  { key: 'employees', label: 'Employees', icon: Briefcase },
  { key: 'banking', label: 'Banking and Finance', icon: Landmark },
  { key: 'forex', label: 'Forex Exchange', icon: Wallet },
];

/* ------------------------------ shared inputs ------------------------------ */

function Field({ label, required, children, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-1.5">
        {label} {required && <span className="text-destructive">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

// Theme tokens throughout, so this screen follows Dark/Light and the accent palette like the rest of the app.
const inputCls =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed';

function TextInput({ value, onChange, ...rest }) {
  return <input className={inputCls} value={value ?? ''} onChange={onChange} {...rest} />;
}
function SelectInput({ value, onChange, children, ...rest }) {
  return (
    <div className="relative">
      <select className={`${inputCls} appearance-none pr-9`} value={value ?? ''} onChange={onChange} {...rest}>
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
    </div>
  );
}
function TextArea({ value, onChange, rows = 4, ...rest }) {
  return <textarea rows={rows} className={`${inputCls} resize-y`} value={value ?? ''} onChange={onChange} {...rest} />;
}
function Checkbox({ checked, onChange, label }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-foreground cursor-pointer select-none">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={onChange}
        className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
      />
      {label}
    </label>
  );
}

function CountryDialCodeSelect({ value, onChange, countries = [] }) {
  // value is the dial_code ("+230"); options show "Mauritius (+230)".
  const list = countries.filter((c) => c.dial_code);
  const hasCurrent = list.some((c) => c.dial_code === value);
  return (
    <SelectInput value={value || ''} onChange={(e) => onChange(e.target.value)}>
      {!list.length && <option value={value || ''}>{value || 'Select code…'}</option>}
      {list.length > 0 && !hasCurrent && value && (
        <option value={value}>{value} (legacy)</option>
      )}
      {list.length > 0 && !value && <option value="">Select code…</option>}
      {list.map((c) => (
        <option key={c.id} value={c.dial_code}>
          {c.name} ({c.dial_code})
        </option>
      ))}
    </SelectInput>
  );
}

/* ------------------------------ main page ------------------------------ */

export default function CompanyProfile() {
  const { user, hasPermission, isPermissionGated } = useAuth();
  const { restaurantId: ctxRestaurantId } = useRestaurant();
  const restaurantId = user?.restaurant_id || ctxRestaurantId;

  // Permission gating — only enforced when the codes have been defined.
  const viewGated = isPermissionGated('company.view');
  const editGated = isPermissionGated('company.edit');
  const canView = !viewGated || hasPermission('company.view');
  const canEdit = !editGated || hasPermission('company.edit');

  const [tab, setTab] = useState('profile');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Master data (loaded once for dropdowns)
  const [companyTypes, setCompanyTypes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    if (!restaurantId) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get(`/users/restaurant/${restaurantId}/company-profile/`);
        if (!cancel && data?.success) setProfile(data.data);
      } catch (_) {
        /* leave profile null; the page shows its loading/empty state */
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [restaurantId]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [cts, curs, ctys] = await Promise.all([
          mastersService.list('companyTypes').catch(() => []),
          mastersService.list('currencies').catch(() => []),
          mastersService.list('countries').catch(() => []),
        ]);
        if (cancel) return;
        setCompanyTypes(cts);
        setCurrencies(curs);
        setCountries(ctys);
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancel = true; };
  }, []);

  const update = (patch) => setProfile((p) => ({ ...p, ...patch }));

  const save = async (fieldsHint = '') => {
    if (!restaurantId || !profile) return;
    setSaving(true);
    try {
      const { data } = await apiClient.put(`/users/restaurant/${restaurantId}/company-profile/`, profile);
      if (data?.success) {
        setProfile(data.data);
        showToast(`Saved${fieldsHint ? ` · ${fieldsHint}` : ''}`, 'success');
      } else {
        showToast(data?.error || 'Save failed', 'error');
      }
    } catch (e) {
      showToast(e?.response?.data?.error || 'Network error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showToast = (msg, kind = 'success') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 2200);
  };

  if (!restaurantId) {
    return (
      <div className="p-8 text-muted-foreground text-sm">
        No restaurant context available. Please sign in again.
      </div>
    );
  }
  if (loading || !profile) {
    return (
      <div className="p-8">
        <div className="animate-pulse text-muted-foreground text-sm">Loading company profile…</div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="min-h-[calc(100vh-56px)] bg-muted flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-destructive/10 text-destructive flex items-center justify-center">
            <Lock size={24} />
          </div>
          <h2 className="text-lg font-semibold text-foreground">Access denied</h2>
          <p className="text-sm text-muted-foreground mt-2">
            Your role does not have permission to view the Company Profile.
          </p>
          <p className="text-xs text-muted-foreground mt-3">
            Ask an administrator to grant the <code className="bg-muted px-1.5 py-0.5 rounded text-[11px]">company.view</code> permission to your role.
          </p>
        </div>
      </div>
    );
  }

  // The active company name drives the page header, auto-seeded server-side so it is populated before any save.
  const companyName = profile.company_name || profile.trading_name || '';

  return (
    <div className="flex min-h-[calc(100vh-56px)] bg-muted">
      {/* Left rail */}
      <aside className="w-64 shrink-0 border-r border-border bg-card">
        <div className="px-6 py-5 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground truncate" title={companyName || 'Company Settings'}>
            {companyName || 'Company Settings'}
          </h2>
        </div>
        <nav className="p-3 space-y-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => !t.soon && setTab(t.key)}
                disabled={t.soon}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors text-left ${
                  active
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : t.soon
                      ? 'text-muted-foreground cursor-not-allowed'
                      : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon size={16} className={active ? 'text-primary-foreground' : ''} />
                <span className="flex-1">{t.label}</span>
                {t.soon && <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Soon</span>}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Main */}
      <main className="flex-1 p-8 overflow-y-auto">
        <div className="w-full">
          {!canEdit && (
            <div className="mb-4 flex items-center gap-3 rounded-md border border-warning/30 bg-warning-soft px-4 py-2.5 text-sm text-warning">
              <Eye size={16} className="text-warning flex-shrink-0" />
              <span>
                <strong>Read-only access.</strong> Your role can view this page but cannot edit it.
              </span>
            </div>
          )}
          {tab === 'profile' && <ProfileTab profile={profile} update={update} save={save} saving={saving} companyTypes={companyTypes} currencies={currencies} countries={countries} canEdit={canEdit} />}
          {tab === 'legal' && <LegalTab profile={profile} update={update} save={save} saving={saving} canEdit={canEdit} />}
          {tab === 'mra' && <MraTab profile={profile} update={update} save={save} saving={saving} canEdit={canEdit} />}
          {tab === 'address' && <AddressTab profile={profile} update={update} save={save} saving={saving} countries={countries} canEdit={canEdit} />}
          {tab === 'directory' && <DirectoryTab restaurantId={restaurantId} showToast={showToast} canEdit={canEdit} />}
          {tab === 'locations' && <LocationsTab restaurantId={restaurantId} />}
          {tab === 'employees' && <EmployeesTab restaurantId={restaurantId} />}
          {tab === 'banking' && <BankingTab restaurantId={restaurantId} showToast={showToast} canEdit={canEdit} currencies={currencies} />}
          {tab === 'forex' && <ForexTab restaurantId={restaurantId} showToast={showToast} canEdit={canEdit} />}
        </div>
      </main>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 px-4 py-2.5 rounded-md text-sm shadow-lg ${
            toast.kind === 'error'
              ? 'bg-destructive text-destructive-foreground'
              // There is no --success-foreground token; white reads on the solid green in both themes.
              : 'bg-success text-white'
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

/* ------------------------------ tabs ------------------------------ */

function SaveBar({ saving, onSave }) {
  return (
    <div className="mt-8 flex items-center gap-3 border-t border-border pt-6">
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-60"
      >
        <Save size={16} />
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

function SectionTitle({ children, sub }) {
  return (
    <div className="mb-8">
      <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">{children}</h1>
      {sub && <p className="text-sm text-muted-foreground mt-2">{sub}</p>}
    </div>
  );
}

function ProfileTab({ profile, update, save, saving, companyTypes, currencies, countries, canEdit = true }) {
  return (
    <fieldset disabled={!canEdit} className="border-0 p-0 m-0">
      <SectionTitle>Company Profile</SectionTitle>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Field label="Company Name" required>
          <TextInput
            value={profile.company_name}
            onChange={(e) => update({ company_name: e.target.value })}
            placeholder="Enter company name"
          />
        </Field>
        <Field label="Established Date">
          <TextInput
            type="date"
            value={profile.established_date || ''}
            onChange={(e) => update({ established_date: e.target.value })}
          />
        </Field>
        <Field label="Company Phone">
          <div className="flex gap-2">
            <div className="w-44 shrink-0">
              <CountryDialCodeSelect
                value={profile.company_phone_code || '+230'}
                onChange={(v) => update({ company_phone_code: v })}
                countries={countries}
              />
            </div>
            <TextInput
              value={profile.company_phone}
              onChange={(e) => update({ company_phone: e.target.value })}
              placeholder="e.g. 1234567"
            />
          </div>
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Field label="Trading Name / DBA">
          <TextInput
            value={profile.trading_name}
            onChange={(e) => update({ trading_name: e.target.value })}
            placeholder="e.g., Acme Corp MU"
          />
        </Field>
        <Field label="Company Type" required hint={!companyTypes.length ? 'Add types in Settings → Masters → Company Types' : undefined}>
          <SelectInput value={profile.company_type} onChange={(e) => update({ company_type: e.target.value })}>
            <option value="">Select…</option>
            {companyTypes.map((t) => (
              <option key={t.id} value={t.code}>{t.name}</option>
            ))}
            {profile.company_type && !companyTypes.find((t) => t.code === profile.company_type) && (
              <option value={profile.company_type}>{profile.company_type} (legacy)</option>
            )}
          </SelectInput>
        </Field>
        <Field label="Industry / Sector" required>
          <TextInput
            value={profile.industry_sector}
            onChange={(e) => update({ industry_sector: e.target.value })}
            placeholder="e.g., Food & Beverage"
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Field label="Company Website">
          <TextInput
            value={profile.company_website}
            onChange={(e) => update({ company_website: e.target.value })}
            placeholder="www.company.mu"
          />
        </Field>
        <Field label="Number of Employees">
          <SelectInput
            value={profile.number_of_employees}
            onChange={(e) => update({ number_of_employees: e.target.value })}
          >
            <option value="">Select…</option>
            {EMPLOYEE_BANDS.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </SelectInput>
        </Field>
        <Field label="Currency" hint={!currencies.length ? 'Add currencies in Settings → Masters → Currencies' : undefined}>
          <SelectInput
            value={profile.currency_code || ''}
            onChange={(e) => update({ currency_code: e.target.value })}
          >
            <option value="">Select…</option>
            {currencies.map((c) => (
              <option key={c.id} value={c.code}>{c.name} ({c.code})</option>
            ))}
            {profile.currency_code && !currencies.find((c) => c.code === profile.currency_code) && (
              <option value={profile.currency_code}>{profile.currency_code} (legacy)</option>
            )}
          </SelectInput>
        </Field>
      </div>

      <Field label="Business Description">
        <TextArea
          value={profile.business_description}
          onChange={(e) => update({ business_description: e.target.value })}
          placeholder="Brief description of business activities…"
          rows={4}
        />
      </Field>

      {canEdit && <SaveBar saving={saving} onSave={() => save('Company Profile')} />}
    </fieldset>
  );
}

// Mauritius restaurant licenses — each maps to a 5-field group on the model.
const RESTAURANT_LICENSES = [
  {
    title: 'Sanitary / Food Premises Certificate',
    authority: 'Ministry of Health',
    icon: Utensils,
    prefix: 'sanitary',
    required: true,
  },
  {
    title: 'Fire Safety Clearance',
    authority: 'Mauritius Fire & Rescue Service',
    icon: Flame,
    prefix: 'fire_safety',
    required: true,
  },
  {
    title: 'Liquor License',
    authority: 'Local Municipal / District Council',
    icon: Wine,
    prefix: 'liquor',
    required: false,
  },
  {
    title: 'Tourism Enterprise License',
    authority: 'Tourism Authority',
    icon: Palmtree,
    prefix: 'tourism',
    required: false,
  },
];

function LegalTab({ profile, update, save, saving, canEdit = true }) {
  return (
    <fieldset disabled={!canEdit} className="border-0 p-0 m-0">
      <SectionTitle>
        Legal & Compliance
      </SectionTitle>

      {/* --- Registry & Tax Identifiers ---------------------------------- */}
      <div className="rounded-lg border border-primary/30 bg-primary/5 px-5 py-4 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={18} className="text-primary" />
          <h3 className="font-semibold text-foreground">Registry & Tax Identifiers</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="BRN (Business Reg. No.)" required>
            <TextInput value={profile.brn} onChange={(e) => update({ brn: e.target.value })} placeholder="C00000000" />
          </Field>
          <Field label="TAN (Tax Account No.)">
            <TextInput value={profile.tan} onChange={(e) => update({ tan: e.target.value })} placeholder="12345678" />
          </Field>
          <Field label="VAT Registration No.">
            <TextInput
              value={profile.vat_registration_no}
              onChange={(e) => update({ vat_registration_no: e.target.value })}
              placeholder="VAT20251234"
            />
          </Field>
          <Field label="Date of Incorporation">
            <TextInput
              type="date"
              value={profile.date_of_incorporation || ''}
              onChange={(e) => update({ date_of_incorporation: e.target.value })}
            />
          </Field>
          <Field label="Country of Incorporation">
            <TextInput value="Mauritius" disabled />
          </Field>
          <Field label="Trade Fee / License No.">
            <TextInput
              value={profile.trade_license_no}
              onChange={(e) => update({ trade_license_no: e.target.value })}
              placeholder="TF-XXXXX"
            />
          </Field>
        </div>
      </div>

      {/* --- Tax & Registry Documents ------------------------------------ */}
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
        Tax & Registry Documents
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
        <DocVaultRow
          title="BRN Document"
          authority="CBRD"
          docField="brn_document_name"
          renewalField="brn_renewal_date"
          remindField="brn_remind_before_days"
          emailField="brn_send_reminder_email"
          profile={profile}
          update={update}
        />
        <DocVaultRow
          title="VAT Certificate"
          authority="MRA"
          docField="vat_document_name"
          renewalField="vat_renewal_date"
          remindField="vat_remind_before_days"
          emailField="vat_send_reminder_email"
          profile={profile}
          update={update}
        />
        <DocVaultRow
          title="TAN Certificate"
          authority="MRA"
          docField="tan_document_name"
          renewalField="tan_renewal_date"
          remindField="tan_remind_before_days"
          emailField="tan_send_reminder_email"
          profile={profile}
          update={update}
        />
        <DocVaultRow
          title="Trade Fee / License"
          authority="Local Authority"
          docField="trade_license_document_name"
          renewalField="trade_license_renewal_date"
          remindField="trade_license_remind_before_days"
          emailField="trade_license_send_reminder_email"
          profile={profile}
          update={update}
        />
      </div>

      {/* --- Restaurant Operating Licenses ------------------------------- */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Restaurant Operating Licenses (Mauritius)
        </h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {RESTAURANT_LICENSES.map((lic) => (
          <DocVaultRow
            key={lic.prefix}
            title={lic.title}
            authority={lic.authority}
            icon={lic.icon}
            required={lic.required}
            numberField={`${lic.prefix}_license_no`}
            docField={`${lic.prefix}_document_name`}
            renewalField={`${lic.prefix}_renewal_date`}
            remindField={`${lic.prefix}_remind_before_days`}
            emailField={`${lic.prefix}_send_reminder_email`}
            profile={profile}
            update={update}
          />
        ))}
      </div>

      {canEdit && <SaveBar saving={saving} onSave={() => save('Legal & Compliance')} />}
    </fieldset>
  );
}

function DocVaultRow({
  title, authority, icon: Icon, required, hint,
  numberField, docField, renewalField, remindField, emailField,
  profile, update,
}) {
  const [open, setOpen] = useState(false);
  const number = numberField ? profile[numberField] : null;
  return (
    <div className="rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm text-foreground"
      >
        <span className="flex items-center gap-2 min-w-0">
          {Icon ? <Icon size={16} className="text-muted-foreground shrink-0" /> : null}
          <span className="font-medium truncate">
            {title}
            {required && <span className="text-destructive ml-1">*</span>}
          </span>
          {authority && (
            <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">
              {authority}
            </span>
          )}
        </span>
        <ChevronDown size={16} className={`transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-border pt-4">
          {hint && <p className="text-xs text-muted-foreground -mt-1">{hint}</p>}
          {numberField && (
            <Field label="License Number">
              <TextInput
                value={number || ''}
                onChange={(e) => update({ [numberField]: e.target.value })}
                placeholder="Enter license / reference number"
              />
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Renewal Date">
              {/* When this licence next falls due - always ahead; the dates above record when something happened. */}
              <TextInput
                type="date"
                min={profile[renewalField] && profile[renewalField] < todayISO()
                  ? profile[renewalField] : todayISO()}
                value={profile[renewalField] || ''}
                onChange={(e) => update({ [renewalField]: e.target.value })}
              />
            </Field>
            <Field label="Remind Before">
              <SelectInput
                value={profile[remindField] || 30}
                onChange={(e) => update({ [remindField]: parseInt(e.target.value) })}
              >
                {REMINDER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </SelectInput>
            </Field>
          </div>
          <Checkbox
            checked={profile[emailField]}
            onChange={(e) => update({ [emailField]: e.target.checked })}
            label={<span className="flex items-center gap-1.5"><Mail size={14} className="text-muted-foreground" /> Send renewal reminder to email</span>}
          />
          <Field label="Attach Attested Document">
            <label className="flex items-center gap-2 cursor-pointer rounded-md border border-dashed border-input px-3 py-2.5 text-sm text-muted-foreground hover:border-muted-foreground/60">
              <Paperclip size={14} />
              {profile[docField] || 'Click to attach attested document (PDF, JPG, PNG)'}
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) update({ [docField]: f.name });
                }}
              />
            </label>
          </Field>
        </div>
      )}
    </div>
  );
}

function MraTab({ profile, update, save, saving, canEdit = true }) {
  return (
    <fieldset disabled={!canEdit} className="border-0 p-0 m-0">
      <SectionTitle>MRA Settings</SectionTitle>

      <div className="mb-4">
        <h3 className="font-semibold text-foreground">MRA E-Invoicing Settings</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="BRN (Business Registration Number)">
          <TextInput
            value={profile.mra_brn}
            onChange={(e) => update({ mra_brn: e.target.value })}
            placeholder="Enter BRN"
          />
        </Field>
        <Field label="VAT Registration Number">
          <TextInput
            value={profile.mra_vat_registration_no}
            onChange={(e) => update({ mra_vat_registration_no: e.target.value })}
            placeholder="Enter VAT number"
          />
        </Field>
        <Field label="Trade Name">
          <TextInput
            value={profile.mra_trade_name}
            onChange={(e) => update({ mra_trade_name: e.target.value })}
            placeholder="Enter trade name"
          />
        </Field>
        <Field label="EBS Counter Number">
          <TextInput
            type="number"
            value={profile.mra_ebs_counter_number}
            onChange={(e) => update({ mra_ebs_counter_number: e.target.value })}
            placeholder="Default: 1"
            min={1}
          />
        </Field>
        <Field label="Default Cashier ID">
          <TextInput
            value={profile.mra_default_cashier_id}
            onChange={(e) => update({ mra_default_cashier_id: e.target.value })}
            placeholder="Overrides username if set"
          />
        </Field>
      </div>

      {canEdit && <SaveBar saving={saving} onSave={() => save('MRA Settings')} />}
    </fieldset>
  );
}

function AddressTab({ profile, update, save, saving, countries = [], canEdit = true }) {
  const billingSame = !!profile.billing_same_as_registered;
  return (
    <fieldset disabled={!canEdit} className="border-0 p-0 m-0">
      <SectionTitle>Addresses &amp; Contact</SectionTitle>

      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
        Primary Contact Information
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <Field label="Primary Contact Name">
          <TextInput
            value={profile.primary_contact_name}
            onChange={(e) => update({ primary_contact_name: e.target.value })}
            placeholder="John Doe"
          />
        </Field>
        <Field label="Designation / Role">
          <TextInput
            value={profile.primary_contact_designation}
            onChange={(e) => update({ primary_contact_designation: e.target.value })}
            placeholder="Managing Director"
          />
        </Field>
        <Field label="Company General Email">
          <TextInput
            type="email"
            value={profile.company_general_email}
            onChange={(e) => update({ company_general_email: e.target.value })}
            placeholder="info@company.mu"
          />
        </Field>
        <Field label="Company Phone Number">
          <div className="flex gap-2">
            <div className="w-44 shrink-0">
              <CountryDialCodeSelect
                value={profile.company_phone_code || '+230'}
                onChange={(v) => update({ company_phone_code: v })}
                countries={countries}
              />
            </div>
            <TextInput
              value={profile.company_phone}
              onChange={(e) => update({ company_phone: e.target.value })}
              placeholder="5 123 4567"
            />
          </div>
        </Field>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Registered Head Office Address
        </h3>
        <span className="text-[10px] font-medium tracking-wider uppercase text-destructive bg-destructive/10 px-2 py-0.5 rounded">
          Statutory
        </span>
      </div>

      <div className="grid grid-cols-1 gap-4 mb-4">
        <Field label="Building / Street Address">
          <TextInput
            value={profile.registered_address_building}
            onChange={(e) => update({ registered_address_building: e.target.value })}
            placeholder="Level X, Building Name, Street Name"
          />
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <Field label="Town / City">
          <TextInput
            value={profile.registered_address_town}
            onChange={(e) => update({ registered_address_town: e.target.value })}
            placeholder="Ebene / Port Louis"
          />
        </Field>
        <Field label="District">
          <TextInput
            value={profile.registered_address_district}
            onChange={(e) => update({ registered_address_district: e.target.value })}
            placeholder="Select a District…"
          />
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <Field label="Postal / Zip Code">
          <TextInput
            value={profile.registered_address_postal_code}
            onChange={(e) => update({ registered_address_postal_code: e.target.value })}
            placeholder="72201"
          />
        </Field>
        <Field label="Country">
          <TextInput
            value={profile.registered_address_country}
            onChange={(e) => update({ registered_address_country: e.target.value })}
            placeholder="Select Country"
          />
        </Field>
      </div>

      <div className="my-6">
        <Checkbox
          checked={billingSame}
          onChange={(e) => update({ billing_same_as_registered: e.target.checked })}
          label="Billing Address is the same as Registered Address"
        />
      </div>

      {!billingSame && (
        <>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
            Billing Address
          </h3>
          <div className="grid grid-cols-1 gap-4 mb-4">
            <Field label="Building / Street Address">
              <TextInput
                value={profile.billing_address_building}
                onChange={(e) => update({ billing_address_building: e.target.value })}
              />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Field label="Town / City">
              <TextInput
                value={profile.billing_address_town}
                onChange={(e) => update({ billing_address_town: e.target.value })}
              />
            </Field>
            <Field label="District">
              <TextInput
                value={profile.billing_address_district}
                onChange={(e) => update({ billing_address_district: e.target.value })}
              />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Field label="Postal / Zip Code">
              <TextInput
                value={profile.billing_address_postal_code}
                onChange={(e) => update({ billing_address_postal_code: e.target.value })}
              />
            </Field>
            <Field label="Country">
              <TextInput
                value={profile.billing_address_country}
                onChange={(e) => update({ billing_address_country: e.target.value })}
              />
            </Field>
          </div>
        </>
      )}

      {canEdit && <SaveBar saving={saving} onSave={() => save('Addresses & Contact')} />}
    </fieldset>
  );
}

/* ------------------------------ Directory ------------------------------ */

function DirectoryTab({ restaurantId, showToast, canEdit = true }) {
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [designationFilter, setDesignationFilter] = useState('');
  const [showModal, setShowModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/users/restaurant/${restaurantId}/directory/`);
      if (data?.success) setEntries(data.data);
    } catch (_) {
      /* leave entries as-is; the table shows its empty state */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    (async () => {
      try {
        const data = await accountsService.list({ type: 'staff', pageSize: 200 });
        setUsers((data?.results || []).filter((u) => u.restaurant_id === restaurantId));
      } catch (_) { /* non-fatal */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return entries.filter((e) => {
      if (designationFilter && e.designation !== designationFilter) return false;
      if (!q) return true;
      return (
        (e.name || '').toLowerCase().includes(q) ||
        (e.designation || '').toLowerCase().includes(q) ||
        (e.email || '').toLowerCase().includes(q)
      );
    });
  }, [entries, query, designationFilter]);

  const designations = useMemo(() => {
    const set = new Set();
    entries.forEach((e) => e.designation && set.add(e.designation));
    return Array.from(set);
  }, [entries]);

  const handleAdd = async (payload) => {
    try {
      const { data } = await apiClient.post(`/users/restaurant/${restaurantId}/directory/`, payload);
      if (data?.success) {
        setEntries((prev) => [...prev, data.data].sort((a, b) => a.name.localeCompare(b.name)));
        setShowModal(false);
        showToast('Person added to directory', 'success');
      } else {
        showToast(data?.error || 'Could not add', 'error');
      }
    } catch (e) {
      showToast(e?.response?.data?.error || 'Network error', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ description: `Remove this entry?`, confirmText: 'Remove', tone: 'destructive' }))) return;
    try {
      const { data } = await apiClient.delete(`/users/restaurant/${restaurantId}/directory/${id}/`);
      if (data?.success) {
        setEntries((prev) => prev.filter((e) => e.id !== id));
        showToast('Entry removed', 'success');
      } else {
        showToast(data?.error || 'Delete failed', 'error');
      }
    } catch (e) {
      showToast(e?.response?.data?.error || 'Delete failed', 'error');
    }
  };

  return (
    <div>
      <SectionTitle>Directory</SectionTitle>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <input
            className={`${inputCls} pl-9`}
            placeholder="Search by name or role…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">⌕</span>
        </div>
        <SelectInput
          value={designationFilter}
          onChange={(e) => setDesignationFilter(e.target.value)}
        >
          <option value="">All Designations</option>
          {designations.map((d) => (<option key={d} value={d}>{d}</option>))}
        </SelectInput>
        {canEdit && (
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
          >
            <Plus size={16} /> Add Person
          </button>
        )}
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 bg-muted text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <div className="col-span-3">Name</div>
          <div className="col-span-2">Designation</div>
          <div className="col-span-2">Phone</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Address</div>
        </div>
        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            No directory entries found. Click "Add Person" to add one.
          </div>
        ) : (
          filtered.map((e) => (
            <div key={e.id} className="grid grid-cols-12 px-4 py-3 border-t border-border text-sm text-foreground hover:bg-muted">
              <div className="col-span-3 font-medium text-foreground">{e.name}</div>
              <div className="col-span-2">{e.designation}</div>
              <div className="col-span-2">{e.phone || '—'}</div>
              <div className="col-span-3">{e.email || '—'}</div>
              <div className="col-span-2 flex items-center justify-between gap-2">
                <span className="truncate">{e.address || '—'}</span>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => handleDelete(e.id)}
                    className="text-muted-foreground hover:text-destructive"
                    title="Remove"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {filtered.length} of {entries.length} entries shown
      </p>

      {showModal && (
        <AddPersonModal
          users={users}
          onClose={() => setShowModal(false)}
          onAdd={handleAdd}
        />
      )}
    </div>
  );
}

function AddPersonModal({ users, onClose, onAdd }) {
  const [staffId, setStaffId] = useState('');
  const [address, setAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!staffId) return;
    setSubmitting(true);
    await onAdd({ staff_id: parseInt(staffId), address });
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-md rounded-lg bg-card shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Add Person to Directory</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-muted-foreground"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="User">
            <SelectInput
              value={staffId}
              onChange={(e) => setStaffId(e.target.value)}
              autoFocus
            >
              <option value="">Select a user…</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name || `${u.first_name} ${u.last_name}`} — {u.staff_type || 'Staff'}
                </option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Address / Location">
            <TextInput
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="e.g., Head Office – Ebene"
            />
          </Field>
        </div>
        <div className="flex items-center justify-end gap-3 px-5 py-4 bg-muted border-t border-border rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm font-medium border border-input text-foreground bg-card hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!staffId || submitting}
            className="px-4 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {submitting ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ Locations ------------------------------ */

function Pill({ tone = 'muted', children }) {
  const tones = {
    muted: 'bg-muted text-muted-foreground',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning-soft text-warning',
    destructive: 'bg-destructive/10 text-destructive',
    primary: 'bg-primary/10 text-primary',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${tones[tone] || tones.muted}`}>
      {children}
    </span>
  );
}

function ManageLink({ to, children }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-input bg-card text-sm font-medium text-foreground hover:bg-muted"
    >
      {children} <ExternalLink size={14} className="text-muted-foreground" />
    </Link>
  );
}

function LocationsTab({ restaurantId }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const rows = await locationsService.list(restaurantId, { includeInactive: true });
        if (!cancel) setLocations(rows);
      } catch (_) {
        /* leave as-is; the table shows its empty state */
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [restaurantId]);

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <SectionTitle sub="The company's branches. Branches are managed on the Locations screen — this register is the company-file view of them.">
          Locations
        </SectionTitle>
        <ManageLink to="/settings/locations">Manage locations</ManageLink>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 bg-muted text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <div className="col-span-5">Branch</div>
          <div className="col-span-2">Group</div>
          <div className="col-span-2">City</div>
          <div className="col-span-2">Phone</div>
          <div className="col-span-1 text-right">Code</div>
        </div>
        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : locations.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            No branches yet. Add them on the Locations screen.
          </div>
        ) : (
          locations.map((l) => (
            <div key={l.id} className="grid grid-cols-12 items-center px-4 py-3 border-t border-border text-sm text-foreground hover:bg-muted">
              <div className="col-span-5 flex items-center gap-2 min-w-0">
                <span className="font-medium truncate">{l.name}</span>
                {l.is_primary && <Pill tone="primary"><Star size={10} /> Primary</Pill>}
                {!l.is_active && <Pill tone="destructive">Inactive</Pill>}
              </div>
              <div className="col-span-2 truncate">{l.group?.name || '—'}</div>
              <div className="col-span-2 truncate">{l.city || '—'}</div>
              <div className="col-span-2 truncate">{l.phone || '—'}</div>
              <div className="col-span-1 text-right text-muted-foreground">{l.order_code || '—'}</div>
            </div>
          ))
        )}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {locations.length} branch{locations.length === 1 ? '' : 'es'} · {locations.filter((l) => l.is_active).length} active
      </p>
    </div>
  );
}

/* ------------------------------ Employees ------------------------------ */

const SHIFT_LABELS = {
  MORNING: 'Morning Shift', AFTERNOON: 'Afternoon Shift', EVENING: 'Evening Shift',
  NIGHT: 'Night Shift', FULL_TIME: 'Full Time', PART_TIME: 'Part Time',
};
const STATUS_TONES = { ACTIVE: 'success', ON_LEAVE: 'warning', INACTIVE: 'muted', TERMINATED: 'destructive' };
const STATUS_LABELS = { ACTIVE: 'Active', ON_LEAVE: 'On Leave', INACTIVE: 'Inactive', TERMINATED: 'Terminated' };

function EmployeesTab({ restaurantId }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const data = await accountsService.list({ type: 'staff', pageSize: 500 });
        const rows = (data?.results || []).filter((s) => String(s.restaurant_id) === String(restaurantId));
        if (!cancel) setStaff(rows);
      } catch (_) {
        /* leave as-is; the table shows its empty state */
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [restaurantId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return staff.filter((s) => {
      if (statusFilter && s.employment_status !== statusFilter) return false;
      if (!q) return true;
      return (
        (s.full_name || '').toLowerCase().includes(q) ||
        (s.employee_id || '').toLowerCase().includes(q) ||
        (s.staff_type || '').toLowerCase().includes(q) ||
        (s.email || '').toLowerCase().includes(q)
      );
    });
  }, [staff, query, statusFilter]);

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <SectionTitle sub="Everyone on the payroll, with their role, shift and employment status. Staff records are managed on the Staff Members screen.">
          Employees
        </SectionTitle>
        <ManageLink to="/staff-management">Manage staff</ManageLink>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <input
            className={`${inputCls} pl-9`}
            placeholder="Search by name, employee ID or role…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">⌕</span>
        </div>
        <SelectInput value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          {Object.entries(STATUS_LABELS).map(([v, lbl]) => (<option key={v} value={v}>{lbl}</option>))}
        </SelectInput>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 bg-muted text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <div className="col-span-1">ID</div>
          <div className="col-span-3">Name</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2">Contact</div>
          <div className="col-span-2">Hired · Shift</div>
          <div className="col-span-2 text-right">Status</div>
        </div>
        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            No employees found.
          </div>
        ) : (
          filtered.map((s) => (
            <div key={s.id} className="grid grid-cols-12 items-center px-4 py-3 border-t border-border text-sm text-foreground hover:bg-muted">
              <div className="col-span-1 text-muted-foreground">{s.employee_id || '—'}</div>
              <div className="col-span-3 font-medium truncate">{s.full_name}</div>
              <div className="col-span-2 truncate">{s.staff_type || '—'}</div>
              <div className="col-span-2 min-w-0">
                <div className="truncate">{s.phone || '—'}</div>
                <div className="truncate text-xs text-muted-foreground">{s.email || ''}</div>
              </div>
              <div className="col-span-2">
                <div>{s.hire_date || '—'}</div>
                <div className="text-xs text-muted-foreground">{SHIFT_LABELS[s.shift] || s.shift || ''}</div>
              </div>
              <div className="col-span-2 text-right">
                <Pill tone={STATUS_TONES[s.employment_status] || 'muted'}>
                  {STATUS_LABELS[s.employment_status] || s.employment_status || '—'}
                </Pill>
              </div>
            </div>
          ))
        )}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {filtered.length} of {staff.length} employees shown
      </p>
    </div>
  );
}

/* ------------------------------ Banking and Finance ------------------------------ */

const ACCOUNT_TYPE_LABELS = {
  bank: 'Bank Account', cash: 'Cash', credit_card: 'Credit Card',
  mobile_money: 'Mobile Money', other: 'Other',
};

const EMPTY_ACCOUNT = {
  name: '', account_type: 'bank', bank_name: '', branch_name: '',
  account_number: '', iban: '', swift_code: '', currency_code: '',
  is_primary: false, is_active: true, notes: '',
};

function BankingTab({ restaurantId, showToast, canEdit = true, currencies = [] }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // EMPTY_ACCOUNT for new, or an account row

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/users/restaurant/${restaurantId}/bank-accounts/`);
      if (data?.success) setAccounts(data.data);
    } catch (_) {
      /* leave as-is; the table shows its empty state */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  const handleSave = async (form) => {
    try {
      const { data } = form.id
        ? await apiClient.put(`/users/restaurant/${restaurantId}/bank-accounts/${form.id}/`, form)
        : await apiClient.post(`/users/restaurant/${restaurantId}/bank-accounts/`, form);
      if (data?.success) {
        setEditing(null);
        showToast(form.id ? 'Account updated' : 'Account added', 'success');
        await load(); // primary can move between rows server-side, so refetch the lot
      } else {
        showToast(data?.error || 'Save failed', 'error');
      }
    } catch (e) {
      showToast(e?.response?.data?.error || 'Network error', 'error');
    }
  };

  const handleDelete = async (account) => {
    if (!(await confirm({
      description: `Remove "${account.name}" from the company file?`,
      confirmText: 'Remove', tone: 'destructive',
    }))) return;
    try {
      const { data } = await apiClient.delete(`/users/restaurant/${restaurantId}/bank-accounts/${account.id}/`);
      if (data?.success) {
        setAccounts((prev) => prev.filter((a) => a.id !== account.id));
        showToast('Account removed', 'success');
      } else {
        showToast(data?.error || 'Delete failed', 'error');
      }
    } catch (e) {
      showToast(e?.response?.data?.error || 'Delete failed', 'error');
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <SectionTitle sub="Where the company banks — account labels, numbers and routing identifiers for invoices and remittances.">
          Banking and Finance
        </SectionTitle>
        {canEdit && (
          <button
            type="button"
            onClick={() => setEditing({ ...EMPTY_ACCOUNT })}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
          >
            <Plus size={16} /> Add Account
          </button>
        )}
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 bg-muted text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <div className="col-span-3">Account</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-2">Bank / Branch</div>
          <div className="col-span-2">Account No.</div>
          <div className="col-span-1">Currency</div>
          <div className="col-span-2 text-right">Status</div>
        </div>
        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : accounts.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            No accounts on file yet. Click "Add Account" to record where the company banks.
          </div>
        ) : (
          accounts.map((a) => (
            <div key={a.id} className="grid grid-cols-12 items-center px-4 py-3 border-t border-border text-sm text-foreground hover:bg-muted">
              <div className="col-span-3 flex items-center gap-2 min-w-0">
                <span className="font-medium truncate">{a.name}</span>
                {a.is_primary && <Pill tone="primary"><Star size={10} /> Primary</Pill>}
              </div>
              <div className="col-span-2">{ACCOUNT_TYPE_LABELS[a.account_type] || a.account_type}</div>
              <div className="col-span-2 min-w-0">
                <div className="truncate">{a.bank_name || '—'}</div>
                {a.branch_name && <div className="truncate text-xs text-muted-foreground">{a.branch_name}</div>}
              </div>
              <div className="col-span-2 min-w-0">
                <div className="truncate">{a.account_number || '—'}</div>
                {(a.iban || a.swift_code) && (
                  <div className="truncate text-xs text-muted-foreground">
                    {[a.iban, a.swift_code].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
              <div className="col-span-1">{a.currency_code || '—'}</div>
              <div className="col-span-2 flex items-center justify-end gap-2">
                <Pill tone={a.is_active ? 'success' : 'muted'}>{a.is_active ? 'Active' : 'Inactive'}</Pill>
                {canEdit && (
                  <>
                    <button
                      type="button"
                      onClick={() => setEditing({ ...a })}
                      className="text-muted-foreground hover:text-foreground"
                      title="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(a)}
                      className="text-muted-foreground hover:text-destructive"
                      title="Remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {editing && (
        <BankAccountModal
          account={editing}
          currencies={currencies}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function BankAccountModal({ account, currencies, onClose, onSave }) {
  const [form, setForm] = useState(account);
  const [submitting, setSubmitting] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    if (!form.name.trim()) return;
    setSubmitting(true);
    await onSave(form);
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-2xl rounded-lg bg-card shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">
            {form.id ? 'Edit Bank Account' : 'Add Bank Account'}
          </h3>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Account Label" required>
            <TextInput
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder='e.g. "MCB Operating Account"'
              autoFocus
            />
          </Field>
          <Field label="Account Type">
            <SelectInput value={form.account_type} onChange={(e) => set({ account_type: e.target.value })}>
              {Object.entries(ACCOUNT_TYPE_LABELS).map(([v, lbl]) => (
                <option key={v} value={v}>{lbl}</option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Bank Name">
            <TextInput value={form.bank_name} onChange={(e) => set({ bank_name: e.target.value })} placeholder="e.g. MCB, SBM, Absa" />
          </Field>
          <Field label="Branch">
            <TextInput value={form.branch_name} onChange={(e) => set({ branch_name: e.target.value })} placeholder="e.g. Port Louis Main" />
          </Field>
          <Field label="Account Number">
            <TextInput value={form.account_number} onChange={(e) => set({ account_number: e.target.value })} />
          </Field>
          <Field label="Currency">
            <SelectInput value={form.currency_code} onChange={(e) => set({ currency_code: e.target.value })}>
              <option value="">Select…</option>
              {currencies.map((c) => (
                <option key={c.id} value={c.code}>{c.name} ({c.code})</option>
              ))}
              {form.currency_code && !currencies.find((c) => c.code === form.currency_code) && (
                <option value={form.currency_code}>{form.currency_code} (legacy)</option>
              )}
            </SelectInput>
          </Field>
          <Field label="IBAN">
            <TextInput value={form.iban} onChange={(e) => set({ iban: e.target.value })} placeholder="MU..." />
          </Field>
          <Field label="SWIFT / BIC">
            <TextInput value={form.swift_code} onChange={(e) => set({ swift_code: e.target.value })} placeholder="e.g. MCBLMUMU" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Notes">
              <TextArea value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={2} />
            </Field>
          </div>
          <div className="md:col-span-2 flex items-center gap-6">
            <Checkbox
              checked={form.is_primary}
              onChange={(e) => set({ is_primary: e.target.checked })}
              label="Primary account (shown on invoices)"
            />
            {form.id && (
              <Checkbox
                checked={form.is_active}
                onChange={(e) => set({ is_active: e.target.checked })}
                label="Active"
              />
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-5 py-4 bg-muted border-t border-border rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm font-medium border border-input text-foreground bg-card hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!form.name.trim() || submitting}
            className="px-4 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ Forex Exchange ------------------------------ */

function StatCard({ label, value, hint }) {
  return (
    <div className="rounded-lg border border-border bg-card px-5 py-4">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold text-foreground mt-1">{value}</p>
      {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}

function ForexTab({ restaurantId, showToast, canEdit = true }) {
  const [summary, setSummary] = useState(null); // { base_code, enabled_count, last_sync, currencies }
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [editing, setEditing] = useState(null); // currency row being edited
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [fx, hist] = await Promise.all([
        apiClient.get(`/users/restaurant/${restaurantId}/forex/`),
        apiClient.get(`/users/restaurant/${restaurantId}/forex/history/`),
      ]);
      if (fx.data?.success) setSummary(fx.data.data);
      if (hist.data?.success) setHistory(hist.data.data);
    } catch (_) {
      /* leave as-is; the table shows its empty state */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  const syncRates = async () => {
    setSyncing(true);
    try {
      const { data } = await apiClient.post(`/users/restaurant/${restaurantId}/forex/refresh-rates/`);
      if (data?.success) {
        const { updated, missing } = data.data;
        showToast(
          `Rates synced · ${updated} updated${missing?.length ? ` · no feed rate for ${missing.join(', ')}` : ''}`,
          'success',
        );
        await load();
      } else {
        showToast(data?.error || 'Sync failed', 'error');
      }
    } catch (e) {
      showToast(e?.response?.data?.error || 'The FX rate feed is unreachable right now.', 'error');
    } finally {
      setSyncing(false);
    }
  };

  const setBase = async (cur) => {
    if (!(await confirm({
      description:
        `Make ${cur.code} the base currency? Every other rate will be re-expressed in ${cur.code} terms, and each change is logged in the rate history.`,
      confirmText: 'Set as base',
    }))) return;
    try {
      const { data } = await apiClient.post(`/users/restaurant/${restaurantId}/forex/${cur.id}/set-base/`);
      if (data?.success) {
        showToast(`${cur.code} is now the base currency`, 'success');
        await load();
      } else {
        showToast(data?.error || 'Could not set base', 'error');
      }
    } catch (e) {
      showToast(e?.response?.data?.error || 'Network error', 'error');
    }
  };

  const handleSaveRate = async (cur, payload) => {
    try {
      const { data } = await apiClient.patch(`/users/restaurant/${restaurantId}/forex/${cur.id}/`, payload);
      if (data?.success) {
        setEditing(null);
        showToast(`${cur.code} updated`, 'success');
        await load();
      } else {
        showToast(data?.error || 'Save failed', 'error');
      }
    } catch (e) {
      showToast(e?.response?.data?.error || 'Network error', 'error');
    }
  };

  const currencies = summary?.currencies || [];
  const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString() : 'Never');

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <SectionTitle sub={`Exchange rates against the base currency${summary?.base_code ? ` (${summary.base_code})` : ''}. Currencies are enabled from the suite-wide SaaSBOXX catalog and rates come from its central FX feed (Bank of Mauritius) or manual entry; buying and selling apply each currency's spread.`}>
          Forex Exchange
        </SectionTitle>
        {canEdit && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={syncRates}
              disabled={syncing}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-input bg-card text-sm font-medium text-foreground hover:bg-muted disabled:opacity-60"
            >
              <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
              {syncing ? 'Syncing…' : 'Sync Rates'}
            </button>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
            >
              <Plus size={16} /> Enable Currency
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <StatCard label="Enabled Currencies" value={loading ? '…' : (summary?.enabled_count ?? 0)} />
        <StatCard
          label="Base Currency"
          value={loading ? '…' : (summary?.base_code || '—')}
          hint="All rates are quoted as base units per 1 unit"
        />
        <StatCard label="Last Rate Sync" value={loading ? '…' : fmtWhen(summary?.last_sync)} />
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 bg-muted text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <div className="col-span-3">Currency</div>
          <div className="col-span-2 text-right">Indicative</div>
          <div className="col-span-2 text-right">Buying / Selling</div>
          <div className="col-span-2 text-center">Base</div>
          <div className="col-span-2">Updated</div>
          <div className="col-span-1 text-right">Status</div>
        </div>
        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : currencies.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            No currencies enabled yet. Add them under Settings → Master Data → Currencies.
          </div>
        ) : (
          currencies.map((c) => (
            <div key={c.id} className="grid grid-cols-12 items-center px-4 py-3 border-t border-border text-sm text-foreground hover:bg-muted">
              <div className="col-span-3 flex items-center gap-2 min-w-0">
                <Coins size={15} className="text-muted-foreground shrink-0" />
                <span className="font-medium">{c.code}</span>
                <span className="truncate text-muted-foreground">{c.name}{c.symbol ? ` · ${c.symbol}` : ''}</span>
              </div>
              <div className="col-span-2 text-right tabular-nums">
                {c.is_base_currency ? '1.000000' : Number(c.exchange_rate).toFixed(6)}
              </div>
              <div className="col-span-2 text-right tabular-nums">
                {c.is_base_currency ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  <>
                    <div>{Number(c.buying_rate).toFixed(6)}</div>
                    <div className="text-xs text-muted-foreground">
                      {Number(c.selling_rate).toFixed(6)} · ±{Number(c.fx_spread_percent)}%
                    </div>
                  </>
                )}
              </div>
              <div className="col-span-2 text-center">
                {c.is_base_currency ? (
                  <Pill tone="primary"><Star size={10} /> Base</Pill>
                ) : canEdit && c.is_active ? (
                  <button
                    type="button"
                    onClick={() => setBase(c)}
                    className="text-xs text-muted-foreground underline-offset-2 hover:underline hover:text-foreground"
                  >
                    Set as base
                  </button>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </div>
              <div className="col-span-2 text-xs text-muted-foreground">{fmtWhen(c.rate_updated_at)}</div>
              <div className="col-span-1 flex items-center justify-end gap-2">
                <Pill tone={c.is_active ? 'success' : 'muted'}>{c.is_active ? 'On' : 'Off'}</Pill>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => setEditing(c)}
                    className="text-muted-foreground hover:text-foreground"
                    title="Edit rate & spread"
                  >
                    <Pencil size={14} />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-8 mb-3">
        Rate History
      </h3>
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 bg-muted text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <div className="col-span-2">Date</div>
          <div className="col-span-2">Currency</div>
          <div className="col-span-2 text-right">Old Rate</div>
          <div className="col-span-2 text-right">New Rate</div>
          <div className="col-span-2">Source</div>
          <div className="col-span-2">Note</div>
        </div>
        {history.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            No rate changes recorded yet. Manual edits, feed syncs and base changes all land here.
          </div>
        ) : (
          history.slice(0, 25).map((h) => (
            <div key={h.id} className="grid grid-cols-12 px-4 py-2.5 border-t border-border text-sm text-foreground">
              <div className="col-span-2 text-muted-foreground">{h.effective_date}</div>
              <div className="col-span-2 font-medium">{h.currency_code}</div>
              <div className="col-span-2 text-right tabular-nums">{Number(h.old_rate).toFixed(6)}</div>
              <div className="col-span-2 text-right tabular-nums">{Number(h.new_rate).toFixed(6)}</div>
              <div className="col-span-2 capitalize">{h.source}</div>
              <div className="col-span-2 truncate text-muted-foreground">{h.note || '—'}</div>
            </div>
          ))
        )}
      </div>
      {history.length > 25 && (
        <p className="mt-2 text-xs text-muted-foreground">Showing the 25 most recent of {history.length} changes.</p>
      )}

      {editing && (
        <ForexRateModal
          currency={editing}
          baseCode={summary?.base_code}
          onClose={() => setEditing(null)}
          onSave={(payload) => handleSaveRate(editing, payload)}
        />
      )}
      {pickerOpen && (
        <CatalogPickerDialog
          restaurantId={restaurantId}
          showToast={showToast}
          onClose={() => setPickerOpen(false)}
          onEnabled={load}
        />
      )}
    </div>
  );
}

function CatalogPickerDialog({ restaurantId, showToast, onClose, onEnabled }) {
  const [catalog, setCatalog] = useState(null); // null = loading, [] = loaded
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [enablingCode, setEnablingCode] = useState('');

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await apiClient.get(`/users/restaurant/${restaurantId}/forex/catalog/`);
        if (cancel) return;
        if (data?.success) setCatalog(data.data);
        else setError(data?.error || 'Could not load the catalog.');
      } catch (e) {
        if (!cancel) setError(e?.response?.data?.error || 'The suite currency catalog is unreachable right now.');
      }
    })();
    return () => { cancel = true; };
  }, [restaurantId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = catalog || [];
    if (!q) return rows;
    return rows.filter((c) =>
      c.code.toLowerCase().includes(q) || (c.name || '').toLowerCase().includes(q));
  }, [catalog, query]);

  const enable = async (row) => {
    setEnablingCode(row.code);
    try {
      const { data } = await apiClient.post(
        `/users/restaurant/${restaurantId}/forex/enable-from-catalog/`,
        { code: row.code, force: row.known }, // re-activating a disabled row is a deliberate click here
      );
      if (data?.success) {
        setCatalog((prev) => (prev || []).map((c) => (c.code === row.code ? { ...c, enabled: true, known: true } : c)));
        showToast(`${row.code} enabled`, 'success');
        onEnabled?.();
      } else {
        showToast(data?.error || 'Could not enable', 'error');
      }
    } catch (e) {
      showToast(e?.response?.data?.error || 'Network error', 'error');
    } finally {
      setEnablingCode('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-lg rounded-lg bg-card shadow-xl flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Enable Currency</h3>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X size={18} />
          </button>
        </div>
        <div className="px-5 pt-4">
          <TextInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the suite catalog by code or name…"
            autoFocus
          />
          <p className="text-xs text-muted-foreground mt-2">
            Currencies are picked from the SaaSBOXX suite catalog, never typed in free-form.
          </p>
        </div>
        <div className="p-5 overflow-y-auto">
          {error ? (
            <div className="px-4 py-8 text-center text-sm text-destructive">{error}</div>
          ) : catalog === null ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">Loading catalog…</div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">No catalog currencies match.</div>
          ) : (
            <div className="rounded-lg border border-border divide-y divide-border">
              {filtered.map((c) => (
                <div key={c.code} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                  <span className="font-medium text-foreground w-12">{c.code}</span>
                  <span className="flex-1 truncate text-foreground">{c.name}</span>
                  <span className="text-muted-foreground w-10 text-right">{c.symbol}</span>
                  {c.enabled ? (
                    <Pill tone="success">Enabled</Pill>
                  ) : (
                    <button
                      type="button"
                      onClick={() => enable(c)}
                      disabled={enablingCode === c.code}
                      className="px-3 py-1 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-60"
                    >
                      {enablingCode === c.code ? 'Enabling…' : 'Enable'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ForexRateModal({ currency, baseCode, onClose, onSave }) {
  const [rate, setRate] = useState(String(currency.exchange_rate));
  const [spread, setSpread] = useState(String(currency.fx_spread_percent));
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const rateNum = parseFloat(rate);
  const spreadNum = parseFloat(spread);
  const valid =
    (currency.is_base_currency || (Number.isFinite(rateNum) && rateNum > 0)) &&
    Number.isFinite(spreadNum) && spreadNum >= 0 && spreadNum <= 100;
  const preview = Number.isFinite(rateNum) && Number.isFinite(spreadNum)
    ? {
        buy: (rateNum * (1 - spreadNum / 100)).toFixed(6),
        sell: (rateNum * (1 + spreadNum / 100)).toFixed(6),
      }
    : null;

  const submit = async () => {
    if (!valid) return;
    setSubmitting(true);
    const payload = { fx_spread_percent: spread };
    if (!currency.is_base_currency) {
      payload.exchange_rate = rate;
      payload.note = note;
    }
    await onSave(payload);
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-md rounded-lg bg-card shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">
            {currency.code} — Rate &amp; Spread
          </h3>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <Field
            label={`Indicative Rate (${baseCode || 'base'} per 1 ${currency.code})`}
            hint={currency.is_base_currency ? 'The base currency is always 1.' : undefined}
          >
            <TextInput
              type="number"
              step="0.000001"
              min="0"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              disabled={currency.is_base_currency}
              autoFocus={!currency.is_base_currency}
            />
          </Field>
          <Field label="FX Spread (%)" hint="Buying = rate − spread · Selling = rate + spread">
            <TextInput
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={spread}
              onChange={(e) => setSpread(e.target.value)}
            />
          </Field>
          {!currency.is_base_currency && preview && (
            <p className="text-sm text-muted-foreground">
              Buying <span className="text-foreground tabular-nums">{preview.buy}</span>
              {' · '}
              Selling <span className="text-foreground tabular-nums">{preview.sell}</span>
            </p>
          )}
          {!currency.is_base_currency && (
            <Field label="Note (kept in the rate history)">
              <TextInput value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional — why the manual change" />
            </Field>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 px-5 py-4 bg-muted border-t border-border rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm font-medium border border-input text-foreground bg-card hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!valid || submitting}
            className="px-4 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
